/**
 * Auth 완료 추적 헬퍼
 * 
 * 로그인/회원가입 리다이렉트 후 콜백 페이지에서 사용
 * - URL 쿼리 파라미터에서 authIntent 확인
 * - 세션 스토리지의 intent:auth와 비교
 * - 일치하면 auth_success 또는 auth_fail 로깅
 * 
 * @example
 * ```tsx
 * // _app.tsx
 * import { trackAuthCompletion } from '@/lib/analytics/react/auth-tracker';
 * 
 * function MyApp() {
 *   const router = useRouter();
 *   const { data: session, status } = useSession();
 *   
 *   useEffect(() => {
 *     const authIntentId = router.query.authIntent as string;
 *     if (authIntentId) {
 *       const tracked = trackAuthCompletion({
 *         authIntentId,
 *         session,
 *         status,
 *       });
 *       
 *       if (tracked) {
 *         // 쿼리 파라미터 제거
 *         const { authIntent, parentIntent, ...rest } = router.query;
 *         router.replace({ query: rest }, undefined, { shallow: true });
 *       }
 *     }
 *   }, [router.query, status, session]);
 * }
 * ```
 */

import { getIntentId, clearIntentId } from '../core/ids';
import { track } from '../core/client';

export interface TrackAuthCompletionParams {
    authIntentId: string;
    session: any;  // NextAuth session
    status: 'authenticated' | 'unauthenticated' | 'loading';
}

export interface TrackAuthCompletionResult {
    tracked: boolean;
    reason?: 'no_intent' | 'intent_mismatch' | 'timeout' | 'loading' | 'success' | 'fail';
}

// 기본 타임아웃 (10분)
const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Auth 완료 추적
 * 
 * @param params - 추적 파라미터
 * @param params.authIntentId - URL에서 전달받은 auth intent ID
 * @param params.session - NextAuth 세션 객체
 * @param params.status - NextAuth 상태 ('authenticated' | 'unauthenticated' | 'loading')
 * @returns 추적 성공 여부와 이유
 */
export function trackAuthCompletion(
    params: TrackAuthCompletionParams
): TrackAuthCompletionResult {
    const { authIntentId, session, status } = params;

    // 로딩 중이면 대기
    if (status === 'loading') {
        return { tracked: false, reason: 'loading' };
    }

    // 세션 스토리지에서 intent:auth 확인
    const authIntent = getIntentId('auth');

    if (!authIntent) {
        // console.log('ℹ️ [AuthTracker] No auth intent in session storage');
        return { tracked: false, reason: 'no_intent' };
    }

    // Intent ID 일치 확인
    if (authIntent.intentId !== authIntentId) {
        console.warn('⚠️ [AuthTracker] Intent ID mismatch', {
            expected: authIntentId,
            found: authIntent.intentId,
        });
        return { tracked: false, reason: 'intent_mismatch' };
    }

    // 타임아웃 확인 (10분 이상 경과)
    const elapsed = Date.now() - authIntent.createdAt;
    if (elapsed > DEFAULT_TIMEOUT_MS) {
        console.warn('⏰ [AuthTracker] Intent timeout', {
            elapsed: Math.round(elapsed / 1000) + 's',
            timeout: Math.round(DEFAULT_TIMEOUT_MS / 1000) + 's',
        });
        clearIntentId('auth');
        return { tracked: false, reason: 'timeout' };
    }

    // Latency 계산
    const latencyMs = Date.now() - authIntent.createdAt;

    // ✅ 인증 성공
    if (status === 'authenticated' && session) {
        const isNewUser = session.user?.isNewUser || false;
        const provider = session.user?.provider || null;
        const methodKind = provider ? 'oauth' : 'password';

        track('auth_success', {
            intent_id: authIntentId,
            // method_kind: methodKind,
            // provider: provider as 'kakao' | 'google' | null,
            // mode_resolved: isNewUser ? 'signup' : 'login',
            latency_ms: latencyMs,
        });

        // console.log(
        //     `✅ [AuthTracker] Success: ${isNewUser ? 'signup' : 'login'} via ${provider || 'password'} (${latencyMs}ms)`
        // );

        clearIntentId('auth');
        return { tracked: true, reason: 'success' };
    }

    // ❌ 인증 실패
    if (status === 'unauthenticated') {
        // 실패한 경우 method_kind, provider를 알 수 없으므로
        // authIntent.payload에서 복원 (start 시 저장한 context)
        const startContext = authIntent.payload;
        const methodKind = startContext?.method_kind || 'oauth';
        const provider = startContext?.provider || null;

        track('auth_fail', {
            intent_id: authIntentId,
            // method_kind: methodKind,
            // provider: provider as 'kakao' | 'google' | null,
            // mode_resolved: 'login',  // 실패 시 기본값
            fail_reason: 'authentication_failed',
        });

        // console.log(
        //     `❌ [AuthTracker] Fail: authentication_failed (${latencyMs}ms)`
        // );

        clearIntentId('auth');
        return { tracked: true, reason: 'fail' };
    }

    return { tracked: false, reason: 'loading' };
}

/**
 * Auth Intent ID를 URL 쿼리에 추가하는 헬퍼
 * 
 * @example
 * ```tsx
 * const callbackUrl = appendAuthIntentToUrl('/detail/NVDA', authIntentId);
 * // → "/detail/NVDA?authIntent=01HX..."
 * 
 * const callbackUrl = appendAuthIntentToUrl(
 *   '/detail/NVDA?tab=feed', 
 *   authIntentId, 
 *   'report_request'
 * );
 * // → "/detail/NVDA?tab=feed&authIntent=01HX...&parentIntent=report_request"
 * ```
 */
export function appendAuthIntentToUrl(
    url: string,
    authIntentId: string,
    parentIntentType?: string
): string {
    const separator = url.includes('?') ? '&' : '?';
    let result = `${url}${separator}authIntent=${encodeURIComponent(authIntentId)}`;

    if (parentIntentType) {
        result += `&parentIntent=${encodeURIComponent(parentIntentType)}`;
    }

    return result;
}

/**
 * URL에서 Auth 추적 파라미터 제거
 * 
 * @example
 * ```tsx
 * const cleaned = removeAuthParamsFromQuery({
 *   authIntent: '01HX...',
 *   parentIntent: 'report_request',
 *   tab: 'feed',
 * });
 * // → { tab: 'feed' }
 * ```
 */
export function removeAuthParamsFromQuery(
    query: Record<string, any>
): Record<string, any> {
    const { authIntent, parentIntent, ...rest } = query;
    return rest;
}

